import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { settingsAPI, SiteSettings } from '../services/api';
import {
  CLINIC_DISPLAY_ADDRESS,
  CLINIC_EMAIL,
  CLINIC_NAME,
  CLINIC_MAP_EMBED_URL,
  CLINIC_PHONE,
  CLINIC_SOCIAL_LINKS,
} from '../config/clinic';

interface SettingsContextProps {
  settings: SiteSettings;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

const defaultSettings: SiteSettings = {
  clinicName: CLINIC_NAME,
  phone: CLINIC_PHONE,
  email: CLINIC_EMAIL,
  address: CLINIC_DISPLAY_ADDRESS,
  googleMapsLink: CLINIC_MAP_EMBED_URL,
  facebookLink: CLINIC_SOCIAL_LINKS.facebook,
  instagramLink: CLINIC_SOCIAL_LINKS.instagram,
  youtubeLink: CLINIC_SOCIAL_LINKS.youtube,
  linkedinLink: CLINIC_SOCIAL_LINKS.linkedin,
};

const SettingsContext = createContext<SettingsContextProps | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    try {
      const res = await settingsAPI.get();
      if (res.data) {
        // Merge fetched settings with defaults to ensure all keys are populated
        setSettings({
          clinicName: res.data.clinicName || defaultSettings.clinicName,
          phone: res.data.phone || defaultSettings.phone,
          email: res.data.email || defaultSettings.email,
          address: res.data.address || defaultSettings.address,
          googleMapsLink: res.data.googleMapsLink || defaultSettings.googleMapsLink,
          facebookLink: res.data.facebookLink || defaultSettings.facebookLink,
          instagramLink: res.data.instagramLink || defaultSettings.instagramLink,
          youtubeLink: res.data.youtubeLink || defaultSettings.youtubeLink,
          linkedinLink: res.data.linkedinLink || defaultSettings.linkedinLink,
          appointmentNotificationEmail: res.data.appointmentNotificationEmail,
        });
      }
    } catch (err) {
      console.warn('SettingsProvider: Failed to fetch settings from API, using defaults.', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshSettings();
  }, [refreshSettings]);

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
